import { CustomLink } from '@/infra/link/CustomLink';
import styles from '@/interface/ui/_shared/items/index.module.scss';
import CalenderButtonGroup from '@/interface/ui/_shared/items/calenderButtonGroup';
import { routes } from '@/config/routes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Launch from '@/domain/launch';
import Event from '@/domain/events';
import { formatPriceToYen } from '@/util/currencyHelper';
import colors from '@/constants/colors';
import { hasProperty } from '@/util/commonUtil';
import { Dispatch, SetStateAction } from 'react';

type Props = {
  items: (Launch | Event)[];
  rowNumber: number;
  userAgent?: string;
  mode?: 'default';
  onStarClick?: () => void;
};

const Items: React.FC<Props> = ({
  items,
  rowNumber = 1,
  userAgent,
  mode,
  onStarClick,
}) => {
  const listBuilder = rowNumber === 1 ? singleRowItems : multiRowsItems;
  const itemsList = listBuilder(items, userAgent as string, mode, onStarClick);

  return itemsList;
};

const renderItem = (
  item: Launch | Event,
  userAgent: string,
  mode?: 'default',
  onStarClick?: () => void
) => {
  if (item instanceof Event || hasProperty(item, 'isEvent')) {
    return (
      <li className={styles.item} key={item.id}>
        <div>
          <CustomLink
            className={styles.noUnderline}
            to={routes.events.detail(item.id.toString())}
            mode={mode}
          >
            <div className={styles.itemBody}>
              <div className={styles.imgContainer}>
                <img
                  className={styles.image}
                  loading="lazy"
                  src={item.coverImage}
                  alt={item.title}
                />
              </div>
              <div className={styles.titleLabel}>
                <div className={styles.title}>{item.title}</div>
              </div>
            </div>
          </CustomLink>

          <div className={styles.itemFooter}>
            <div />
            {!!item.canCalendar && (
              <CalenderButtonGroup
                item={item}
                userAgent={userAgent}
                position={'left'}
              />
            )}
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className={styles.item} key={item.id}>
      <div>
        <CustomLink
          className={styles.noUnderline}
          to={routes.detail(item.id.toString())}
          mode={mode}
        >
          <div className={styles.itemBody}>
            {/* <div className={styles.brand}>{item.brandName}</div> */}
            <div className={styles.imgContainer}>
              <img
                className={styles.image}
                loading="lazy"
                src={item.coverImage}
                alt={item.title}
              />
            </div>
            <div className={styles.titleLabel}>
              <div className={styles.title}>{item.title}</div>
            </div>
            <div className={styles.price}>{formatPriceToYen(item.price)}</div>
          </div>
        </CustomLink>
        <div className={styles.itemFooter}>
          <div className={styles.likeCount}>
            <FontAwesomeIcon
              icon="star"
              color={colors.lightGray}
              size="lg"
              className={styles.starIcon}
              onClick={() => onStarClick}
            />
            <span>{item.likeCount}</span>
          </div>
          {!item.releaseAtDisplay && item.isLaunch && (
            <CalenderButtonGroup
              item={item}
              userAgent={userAgent}
              position={'left'}
            />
          )}
        </div>
      </div>
    </li>
  );
};

function singleRowItems(
  items: (Launch | Event)[],
  userAgent: string,
  mode?: 'default',
  onStarClick?: () => void
) {
  return (
    <ul className={styles.singleRowItems}>
      {items &&
        items.map((item) => {
          return renderItem(item, userAgent, mode, onStarClick);
        })}
    </ul>
  );
}

const renderMultiRowsItem = (
  item: Launch | Event,
  userAgent: string,
  mode?: 'default',
  onStarClick?: () => void
) => {
  if (item instanceof Event || hasProperty(item, 'isEvent')) {
    return (
      <li className={styles.multiRowsItem} key={item.id}>
        <div>
          <CustomLink
            className={styles.noUnderline}
            to={routes.events.detail(item.id.toString())}
            mode={mode}
          >
            <div className={styles.multiRowsItemBody}>
              {multiRowsImage(item)}
              <div className={styles.titleLabel}>
                <div className={styles.title}>{item.title}</div>
              </div>
            </div>
          </CustomLink>
          <div className={styles.multiRowsItemFooter}>
            <div />
            {!!item.canCalendar && (
              <CalenderButtonGroup
                item={item}
                userAgent={userAgent}
                position={'left'}
              />
            )}
          </div>
        </div>
      </li>
    );
  }

  // ListsItemの場合
  return (
    <li className={styles.multiRowsItem} key={item.id}>
      <div>
        <CustomLink
          className={styles.noUnderline}
          to={routes.detail(item.id.toString())}
          mode={mode}
        >
          <div className={styles.multiRowsItemBody}>
            {multiRowsImage(item)}
            <div className={styles.titleLabel}>
              <div className={styles.title}>{item.title}</div>
            </div>
            <div className={styles.price}>{formatPriceToYen(item.price)}</div>
          </div>
        </CustomLink>
        <div className={styles.multiRowsItemFooter}>
          <div className={styles.likeCount}>
            <FontAwesomeIcon
              icon="star"
              color={colors.lightGray}
              size="lg"
              className={styles.starIcon}
              onClick={() => onStarClick}
            />
            <span>{item.likeCount}</span>
          </div>
          {!item.releaseAtDisplay && item.isLaunch && (
            <CalenderButtonGroup
              item={item}
              userAgent={userAgent}
              position={'left'}
            />
          )}
        </div>
      </div>
    </li>
  );
};

function multiRowsItems(
  items: (Launch | Event)[],
  userAgent: string,
  mode?: 'default',
  onStarClick?: () => void
) {
  return (
    <ul className={styles.multiRowsItems}>
      {items &&
        items.map((item) => {
          return renderMultiRowsItem(item, userAgent, mode, onStarClick);
        })}
    </ul>
  );
}

function multiRowsImage(item: Launch | Event) {
  return (
    <img
      className={styles.image}
      loading="lazy"
      src={item.coverImage}
      alt={item.title}
    />
  );
}

export default Items;
